import React, { useEffect, useState, useCallback } from 'react';
import { gccFlowsService } from '../../services/gccFlowsService';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import PlotlyChart from '../../components/charts/PlotlyChart';
import CollapsibleSection from '../signals/components/CollapsibleSection';

const MARKETS = ['', 'ADX', 'DFM', 'Qatar', 'Kuwait', 'Saudi Arabia'];

const fmt = (v, dp = 2) => {
  if (v == null || isNaN(v)) return '-';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: dp });
};
const signClass = (n) => (n == null ? '' : Number(n) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400');

const TickerDetailPage = ({ filterParams }) => {
  const [query, setQuery] = useState('');
  const [marketFilter, setMarketFilter] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [selected, setSelected] = useState(null); // { market, ticker }
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async () => {
    setSearching(true);
    try {
      const res = await gccFlowsService.searchTickers({ q: query, market: marketFilter || undefined });
      setResults(res.data || []);
    } finally {
      setSearching(false);
    }
  }, [query, marketFilter]);

  useEffect(() => {
    const t = setTimeout(search, 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadDetail = useCallback(async (market, ticker) => {
    setLoading(true);
    setSelected({ market, ticker });
    setDetail(null);
    try {
      const res = await gccFlowsService.getTickerDetail(market, ticker, filterParams);
      setDetail(res.data);
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  // Refetch detail when filterParams change for currently selected ticker
  useEffect(() => {
    if (selected) loadDetail(selected.market, selected.ticker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);

  const series = detail?.series || [];
  const dates = series.map((s) => s.report_date);

  return (
    <div>
      <CollapsibleSection title="Search Ticker">
        <div className="flex flex-col md:flex-row gap-2">
          <select
            value={marketFilter}
            onChange={(e) => setMarketFilter(e.target.value)}
            className="text-sm px-3 py-1.5 rounded-md bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300"
          >
            {MARKETS.map((m) => <option key={m} value={m}>{m || 'All markets'}</option>)}
          </select>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search ticker (e.g. ADNOCDRI, EMAAR, IQCD)"
            className="flex-1 text-sm px-3 py-1.5 rounded-md bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-[#c9a96e]/30 focus:border-[#c9a96e] outline-none"
          />
        </div>

        {searching ? (
          <div className="text-xs text-gray-400 mt-2">Searching...</div>
        ) : results.length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-3">No matches.</div>
        ) : (
          <div className="mt-3 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-800">
            {results.map((r, i) => (
              <button
                key={`${r.market}-${r.ticker}-${i}`}
                onClick={() => loadDetail(r.market, r.ticker)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                  selected?.market === r.market && selected?.ticker === r.ticker ? 'bg-[#c9a96e]/5' : ''
                }`}
              >
                <span className="font-medium text-gray-800 dark:text-gray-200">{r.ticker}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{r.market}</span>
              </button>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {!selected ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-800 rounded-lg">
          Pick a ticker above to see its time series.
        </div>
      ) : loading ? (
        <LoadingSpinner message={`Loading ${selected.ticker}...`} />
      ) : !series.length ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          No data for {selected.ticker} in selected period.
        </div>
      ) : (
        <>
          <CollapsibleSection title={`${selected.ticker} (${selected.market}) — Net Foreign Flow ($mn)`}>
            <PlotlyChart
              data={[
                {
                  x: dates,
                  y: series.map((s) => s.net_usd_1d),
                  name: '1-day net',
                  type: 'bar',
                  marker: { color: series.map((s) => (Number(s.net_usd_1d) >= 0 ? '#22c55e' : '#ef4444')) },
                },
                {
                  x: dates,
                  y: series.map((s) => s.net_usd_ytd),
                  name: 'YTD net',
                  type: 'scatter',
                  mode: 'lines',
                  yaxis: 'y2',
                  line: { color: '#c9a96e', width: 2 },
                },
              ]}
              layout={{
                showlegend: true,
                legend: { orientation: 'h', y: -0.2 },
                hovermode: 'x unified',
                margin: { t: 20, b: 80, l: 50, r: 50 },
                yaxis: { title: '1d net ($mn)', zeroline: true },
                yaxis2: { title: 'YTD net ($mn)', overlaying: 'y', side: 'right', zeroline: false },
              }}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Foreign Ownership Trend">
            <PlotlyChart
              data={[
                {
                  x: dates,
                  y: series.map((s) => (s.total_fo_pct == null ? null : Number(s.total_fo_pct) * 100)),
                  name: 'Total FO %',
                  type: 'scatter',
                  mode: 'lines',
                  line: { color: '#3b82f6', width: 2 },
                },
                {
                  x: dates,
                  y: series.map((s) => (s.fol_pct == null ? null : Number(s.fol_pct) * 100)),
                  name: 'FOL %',
                  type: 'scatter',
                  mode: 'lines',
                  line: { color: '#94a3b8', dash: 'dash' },
                },
              ]}
              layout={{
                showlegend: true,
                legend: { orientation: 'h', y: -0.2 },
                hovermode: 'x unified',
                margin: { t: 20, b: 80, l: 50, r: 20 },
                yaxis: { title: '% of mcap' },
              }}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Recent Daily Detail" defaultOpen={false}>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-[#0d1117] sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Date</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">1d $mn</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">WTD</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">MTD</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">YTD</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">FO %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {[...series].reverse().map((r, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{r.report_date}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${signClass(r.net_usd_1d)}`}>{fmt(r.net_usd_1d, 2)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${signClass(r.net_usd_wtd)}`}>{fmt(r.net_usd_wtd, 2)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${signClass(r.net_usd_mtd)}`}>{fmt(r.net_usd_mtd, 2)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${signClass(r.net_usd_ytd)}`}>{fmt(r.net_usd_ytd, 2)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                        {r.total_fo_pct == null ? '-' : `${(Number(r.total_fo_pct) * 100).toFixed(2)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        </>
      )}
    </div>
  );
};

export default TickerDetailPage;
